import type { ClimateState, EnsoPhase, GameEvent, HeatwaveState, PlaceSst, Sanctuary, World } from '../types';
import type { Rng } from '../rng';
import type { RegionId } from '../data/oceans';
import { ANALOG_YEARS, ENSO_TRANSITION, ENSO_YEARS, YEAR_IN_PROGRESS } from '../data/climate/oni';
import { REGION_SST } from '../data/climate/ersstRegions';
import { SITES } from '../data/sites';
import { civilFromEpochDay, dayOf366, epochDayFromCivil, epochDayOf } from '../calendar';
import { held } from '../held';
import { fromCenti, recordedC, shiftedC, type SstRecord } from '../sstRecord';
import { JULY, PHASES, ensoYearLabel, ensoYearOfMonth, stationaryShares } from '../enso';
import { MAX_GAP_DAYS, NO_HEATWAVE, categoryOf, nextHeatwave } from '../heatwave';

/**
 * The ocean's climate from year to year (M24): El Niño and La Niña, each
 * place's departure from its normal, and marine heatwaves. Draws on the
 * climate stream (`climateRngState`), twice a year at most, and on nothing
 * else.
 *
 * **The record first, then the chain.** The game runs on real dates (M12),
 * so where the observed record has the day, the day is the record's: the
 * Oceanic Niño Index's phase for the year (`data/climate/oni.ts`), the
 * aquarium's departure from OISST (`oisstMauiNui.ts`), each region's from
 * ERSST (`ersstRegions.ts`). Beyond the record, each ENSO year (July to June)
 * takes its phase from the last one by the chain counted from the index since
 * 1949-50 (one draw), and plays out as an observed year of that phase from
 * 1982-83 on, chosen evenly (one draw): its departures, day for day, moved
 * along each place's trend by the years between. The year the record has
 * begun and not finished keeps the phase its latest season shows and plays on
 * as the analog the index build chose for it.
 *
 * **One analog year for the whole ocean** keeps the regions and the aquarium
 * in step: an El Niño drawn for the Pacific is the same El Niño in the Indian
 * Ocean's warming and at Maui Nui.
 *
 * **Across the change of year**, where a place's departures would jump from
 * one year's to another's, what was carried is kept and fades: today's
 * departure is the new year's plus the old one's last day's difference from
 * it, falling by e every `decorrelationDays` (the place's own persistence,
 * MEASURED from its record). For a departure that persists as the record's
 * do, that keeps its spread through the change.
 *
 * **The trend continued.** An analog year's departures are moved by the
 * record's least-squares trend times the years between it and today: the
 * warming of 1982 to 2026, carried on in a straight line. No emissions
 * scenario (open, M24).
 */

const OBSERVED = new Map(ENSO_YEARS.map((y) => [y.year, y.phase]));

/** The ENSO year a day is in: the year its July is in. */
function ensoYearOf(epochDay: number): number {
  const { year, month } = civilFromEpochDay(epochDay);
  return ensoYearOfMonth(year, month);
}

/** The chain's long-run shares (`stationaryShares`). */
export const ENSO_STATIONARY: Readonly<Record<EnsoPhase, number>> = stationaryShares(ENSO_TRANSITION);

function phaseFrom(u: number, shares: Readonly<Record<EnsoPhase, number>>): EnsoPhase {
  let cumulative = 0;
  for (const phase of PHASES) {
    cumulative += shares[phase];
    if (u < cumulative) return phase;
  }
  return 'nina';
}

/**
 * An ENSO year's climate: the record's where it has the year, the year in
 * progress's, else drawn from the chain after `previous` (from its long-run
 * shares when there is none: a world opening beyond the record).
 */
export function ensoYearClimate(year: number, previous: EnsoPhase | null, rng: Rng): ClimateState {
  if (YEAR_IN_PROGRESS !== null && year === YEAR_IN_PROGRESS.year) return { ensoYear: year, phase: YEAR_IN_PROGRESS.phase, analogYear: YEAR_IN_PROGRESS.analogYear };
  const observed = OBSERVED.get(year);
  if (observed !== undefined) return { ensoYear: year, phase: observed, analogYear: year };
  const phase = phaseFrom(rng.next(), previous === null ? ENSO_STATIONARY : ENSO_TRANSITION[previous]);
  return { ensoYear: year, phase, analogYear: rng.pick(ANALOG_YEARS[phase]) };
}

/** The first day the daily record has, before which no world can open (the analog years' first July). */
const EARLIEST_START_EPOCH_DAY = epochDayFromCivil(1982, JULY, 1);

/** The climate of the ENSO year a world opens in. */
export function openingClimate(startEpochDay: number, rng: Rng): ClimateState {
  if (startEpochDay < EARLIEST_START_EPOCH_DAY) throw new Error('A world cannot open before July 1982: the daily record begins then.');
  return ensoYearClimate(ensoYearOf(startEpochDay), null, rng);
}

const regionRecords = new Map<RegionId, SstRecord>();

/** A region's record (ERSST v5, monthly). */
export function regionRecord(id: RegionId): SstRecord {
  let record = regionRecords.get(id);
  if (!record) {
    record = { kind: 'monthly', source: REGION_SST.source, first: REGION_SST.first, last: REGION_SST.last, ...REGION_SST.regions[id] };
    regionRecords.set(id, record);
  }
  return record;
}

const siteRecords = new Map<string, SstRecord>();

/** A sanctuary's record: its site's own, daily, or its region's where the site has none. */
export function sanctuaryRecord(sanctuary: Pick<Sanctuary, 'siteId' | 'regionId'>): SstRecord {
  const daily = SITES[sanctuary.siteId].sst;
  if (!daily) return regionRecord(sanctuary.regionId);
  let record = siteRecords.get(sanctuary.siteId);
  if (!record) {
    record = { kind: 'daily', ...daily };
    siteRecords.set(sanctuary.siteId, record);
  }
  return record;
}

/** Whether the regions' record (ERSST v5, one record end for all twelve) has the day. */
export function regionsObserved(epochDay: number): boolean {
  return recordedC(regionRecord('north_pacific_low'), epochDay) !== null;
}

/** How many years back a place reads its record today: none while the record has the day, else the analog year's. */
function shiftOn(record: SstRecord, climate: ClimateState, epochDay: number): number {
  return recordedC(record, epochDay) !== null ? 0 : climate.ensoYear - climate.analogYear;
}

/** A place's departure from its normal today, °C: its record, read as `sst` says. */
export function departureC(record: SstRecord, sst: PlaceSst, epochDay: number): number {
  return held(shiftedC(record, epochDay, sst.shiftYears) + sst.offsetC);
}

/** Where a place reads its record on the day a world opens: nothing carried. */
export function openingSst(record: SstRecord, climate: ClimateState, epochDay: number): PlaceSst {
  return { shiftYears: shiftOn(record, climate, epochDay), offsetC: 0 };
}

/**
 * Today's reading for a place, from yesterday's: the same years back and what
 * was carried fading, or, where the shift has changed, yesterday's departure
 * carried across.
 */
function nextSst(record: SstRecord, sst: PlaceSst, yesterdayC: number, climate: ClimateState, epochDay: number): PlaceSst {
  const shiftYears = shiftOn(record, climate, epochDay);
  const fade = Math.exp(-1 / record.decorrelationDays);
  const carried = shiftYears === sst.shiftYears ? sst.offsetC : yesterdayC - shiftedC(record, epochDay, shiftYears);
  return { shiftYears, offsetC: held(carried * fade) };
}

const NAMES: Readonly<Record<EnsoPhase, string>> = { nino: 'El Niño', neutral: 'neutral', nina: 'La Niña' };

/** An ENSO phase as the readout names it. */
export function phaseName(phase: EnsoPhase): string {
  return NAMES[phase];
}

/**
 * Step 1 of the tick, before the environment: on the first day of an ENSO
 * year, the year's climate; every day, where each place reads its record. The
 * environment then reads each place's departure (`departureC`), and the
 * heatwaves follow it (`stepHeatwaves`).
 */
export function stepClimate(world: World, rng: Rng, events: GameEvent[]): void {
  const today = epochDayOf(world);
  const year = ensoYearOf(today);
  if (year !== world.climate.ensoYear) {
    world.climate = ensoYearClimate(year, world.climate.phase, rng);
    events.push({
      day: world.day,
      severity: 'info',
      title: `${ensoYearLabel(year)}: ${world.climate.phase === 'neutral' ? 'a neutral year' : NAMES[world.climate.phase]} in the Pacific`,
      // What the regions' record shows with the index (`ersstRegions.ts`): the
      // tropical Pacific's bands at 0.45 and 0.50, the tropical Indian at 0.33;
      // Maui Nui's own surface only 0.21 (MEASURED, review M25), so it is not
      // named.
      detail: world.climate.phase === 'neutral'
        ? 'The tropical Pacific is near its normal this year: neither El Niño nor La Niña.'
        : world.climate.phase === 'nino'
          ? 'The eastern tropical Pacific runs warm this year, and the tropical Indian Ocean with it.'
          : 'The eastern tropical Pacific runs cool this year, and the tropical Indian Ocean with it.',
    });
  }
  for (const state of world.regions) {
    state.sst = nextSst(regionRecord(state.id), state.sst, state.env.seaSurfaceAnomalyC, world.climate, today);
  }
  for (const s of world.sanctuaries) {
    s.sst = nextSst(sanctuaryRecord(s), s.sst, s.env.seaSurfaceAnomalyC, world.climate, today);
  }
}

/** The marine heatwave threshold's height above the normal on a day, °C, where the record has one. */
function thresholdC(record: SstRecord, epochDay: number): number | null {
  return record.kind === 'daily' ? fromCenti(record.thresholdCentiC[dayOf366(epochDay) - 1] as number) : null;
}

/**
 * After the environment: each sanctuary with a daily record follows its
 * heatwave, and the log says when one begins and ends, by the place, which a
 * rename does not change (a save replayed from before v6 knows only today's
 * name). A sanctuary reading its region's monthly record has none: a month's
 * mean cannot show five days.
 */
export function stepHeatwaves(world: World, events: GameEvent[]): void {
  const today = epochDayOf(world);
  for (const s of world.sanctuaries) {
    const threshold = thresholdC(sanctuaryRecord(s), today);
    if (threshold === null) continue;
    const before = s.heatwave;
    s.heatwave = nextHeatwave(before, held(s.env.seaSurfaceAnomalyC / threshold), today);
    if (before.startEpochDay === null && s.heatwave.startEpochDay !== null) {
      events.push({
        day: world.day,
        severity: 'warning',
        title: `A marine heatwave around ${s.site.placeName}`,
        detail: `The surface has been above the 90th percentile for its date for ${s.heatwave.daysAbove} days `
          + `(category ${categoryOf(s.heatwave.peakRatio)} so far).`,
      });
    }
    if (before.startEpochDay !== null && s.heatwave.startEpochDay === null) {
      const days = today - MAX_GAP_DAYS - before.startEpochDay;
      events.push({
        day: world.day,
        severity: 'info',
        title: `The marine heatwave around ${s.site.placeName} is over`,
        detail: `It lasted ${days} days and reached category ${categoryOf(before.peakRatio)}.`,
      });
    }
  }
}

/** The heatwave the readout shows: its day and today's departure as a multiple of the threshold's, or null. */
export function heatwaveToday(s: Sanctuary, epochDay: number): { day: number; ratio: number } | null {
  if (s.heatwave.startEpochDay === null) return null;
  const threshold = thresholdC(sanctuaryRecord(s), epochDay);
  return threshold === null ? null : { day: epochDay - s.heatwave.startEpochDay + 1, ratio: s.env.seaSurfaceAnomalyC / threshold };
}

/**
 * The days before a world opens that its heatwave is followed over, to know
 * whether one is under way: longer than the record's longest event (MEASURED,
 * 253 days, from May 2019, `oisstMauiNui.ts`).
 */
const LOOKBACK_DAYS = 365;

/**
 * A sanctuary's heatwave on the day a world opens: followed over the year
 * before it in the record, where the record has that year, so a world that
 * opens during one opens in it; else none.
 */
export function openingHeatwave(record: SstRecord, startEpochDay: number): HeatwaveState {
  let h: HeatwaveState = { ...NO_HEATWAVE };
  if (record.kind !== 'daily' || recordedC(record, startEpochDay - LOOKBACK_DAYS) === null || recordedC(record, startEpochDay) === null) return h;
  for (let d = startEpochDay - LOOKBACK_DAYS; d <= startEpochDay; d++) {
    h = nextHeatwave(h, held(held(recordedC(record, d) as number) / (thresholdC(record, d) as number)), d);
  }
  return h;
}
